import { useState } from 'react'
import type { ReactElement } from 'react'

import { Button, InputText } from '../../components'
import { getErrorCode } from '../../lib/errorCode'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import styles from './ChangePasswordPage.module.scss'

/**
 * Changing one's own password — the one account screen there is, reached from the
 * shell's account controls.
 *
 * `auth.changePassword` revokes every refresh token of this person and then
 * re-sets THIS tab's cookie pair from the same response: the person who just
 * succeeded stays signed in here and every other session of theirs is dead,
 * which is the point of the rotation. So there is nothing to do with the
 * `accessToken` that comes back, and nothing to navigate away from — the screen
 * says what happened and stays put.
 */

/** The API's floor (`auth.changePassword` takes `min(8)`). */
const MIN_PASSWORD_LENGTH = 8

/**
 * The one place this screen does not use the shared copy. A wrong current
 * password comes back as UNAUTHORIZED — the same code as a dead session — and the
 * generic 'tu sesión ha caducado' would be a lie: the session is alive, it is the
 * typed password that is wrong. A session that really is over never reaches this
 * message anyway: `auth.me` fails with it and `RequireAuth` lands on /login.
 */
const changePasswordError = (error: unknown): string =>
  getErrorCode(error) === 'UNAUTHORIZED'
    ? 'La contraseña actual no es correcta.'
    : trpcMessage(error)

export const ChangePasswordPage = (): ReactElement => {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const changePassword = trpc.auth.changePassword.useMutation({
    onSuccess: () => {
      setCurrentPassword('')
      setNewPassword('')
      setDone(true)
    },
    onError: (error) => setErrorMessage(changePasswordError(error)),
  })

  return (
    <section className={styles['change-password-page']}>
      <h1 className={styles['change-password-page__title']}>Cambiar contraseña</h1>

      <form
        className={styles['change-password-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          setErrorMessage(null)
          setDone(false)
          changePassword.mutate({ currentPassword, newPassword })
        }}
      >
        <InputText
          label="Contraseña actual"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          required
        />
        <InputText
          label="Contraseña nueva"
          type="password"
          autoComplete="new-password"
          hint={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres.`}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          required
        />

        {errorMessage !== null && (
          <p role="alert" className={styles['change-password-page__error']}>{errorMessage}</p>
        )}
        {done && (
          <p role="status" className={styles['change-password-page__ok']}>
            Contraseña cambiada. Sigues dentro en esta pantalla; las demás sesiones se han cerrado.
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          fullWidth
          disabled={
            changePassword.isPending || currentPassword === '' || newPassword.length < MIN_PASSWORD_LENGTH
          }
          label={changePassword.isPending ? 'Guardando…' : 'Guardar'}
        />
      </form>
    </section>
  )
}
